import { useState, useEffect } from 'react';
import { prettyJson } from '../components/auditData';
import { Collapse, Input, Modal, notification, Popover, Spin, Timeline } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import APIClient from '../api/api';
import { Alert, AlertAuditLogEntry, AlertHistory, AlertType, ListAlertsResponse } from '../api/models';
import moment from 'moment-timezone';
import {
  Button,
  Column,
  DataTable,
  FilterPill,
  Pagination,
  StatusTag,
  TagTone,
} from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';
import AutoRenewExpiringTable from '../components/AutoRenewExpiringTable';

const { TextArea } = Input;

const DEFAULT_pageSize = 20;

const statusOptions: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const statusTone: Record<string, TagTone> = {
  active: 'danger',
  resolved: 'accent',
  dismissed: 'success',
};

const Alerts = () => {
  const navigate = useNavigate();
  const { alertId: alertIdParam } = useParams<{ alertId?: string }>();
  const [alerts, setAlerts] = useState<ListAlertsResponse>();
  const [alertTypes, setAlertTypes] = useState<AlertType[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [alertTypeFilter, setAlertTypeFilter] = useState<number | undefined>();
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_pageSize);

  const [detailModal, setDetailModal] = useState<Alert | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [history, setHistory] = useState<AlertHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<AlertAuditLogEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  useEffect(() => {
    loadAlertTypes();
  }, []);

  useEffect(() => {
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, alertTypeFilter, search, page, pageSize]);

  async function loadAlertTypes() {
    try {
      const resp = await APIClient.listAlertTypes();
      setAlertTypes(resp.alertTypes);
    } catch (e) {
      notification.error({ message: 'Failed to load alert types.' });
    }
  }

  async function loadAlerts() {
    setLoading(true);
    try {
      const resp = await APIClient.listAlerts(
        { page, pageSize: pageSize },
        {
          status: statusFilter || undefined,
          alertTypeId: alertTypeFilter,
          search: search || undefined,
        },
      );
      setAlerts(resp);
    } catch (e) {
      notification.error({ message: 'Failed to load alerts.' });
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory(alertId: number) {
    setHistoryLoading(true);
    try {
      const resp = await APIClient.getAlertHistory(alertId);
      setHistory(resp.history);
    } catch (e) {
      notification.error({ message: 'Failed to load alert history.' });
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadAuditLog(alertId: number) {
    setAuditLogLoading(true);
    try {
      const resp = await APIClient.getAlertAuditLog(alertId);
      setAuditLog(resp.auditLog);
    } catch (e) {
      notification.error({ message: 'Failed to load audit log.' });
    } finally {
      setAuditLogLoading(false);
    }
  }

  async function handleDismiss(alertItem: Alert) {
    setDismissing(true);
    try {
      await APIClient.dismissAlert(alertItem.id);
      notification.success({ message: 'Alert dismissed.' });
      loadAlerts();
      closeDetail();
    } catch (e) {
      notification.error({ message: 'Failed to dismiss alert.' });
    } finally {
      setDismissing(false);
    }
  }

  async function handleSaveNotes() {
    if (!detailModal) return;
    setSavingNotes(true);
    try {
      const updated = await APIClient.updateAlertNotes(detailModal.id, notesValue);
      notification.success({ message: 'Notes updated.' });
      setDetailModal(updated);
      loadAlerts();
    } catch (e) {
      notification.error({ message: 'Failed to update notes.' });
    } finally {
      setSavingNotes(false);
    }
  }

  function openDetail(record: Alert) {
    setDetailModal(record);
    setNotesValue(record.notes || '');
    setHistory([]);
    setAuditLog([]);
    loadHistory(record.id);
    loadAuditLog(record.id);
    navigate(`/alerts/${record.id}`);
  }

  function closeDetail() {
    setDetailModal(null);
    navigate('/alerts');
  }

  // Open the modal when the URL is /alerts/:alertId (shared links, browser nav).
  useEffect(() => {
    if (!alertIdParam) return;
    const id = Number(alertIdParam);
    if (!Number.isFinite(id) || id <= 0) return;
    if (detailModal?.id === id) return;
    (async () => {
      try {
        const fetched = await APIClient.getAlert(id);
        setDetailModal(fetched);
        setNotesValue(fetched.notes || '');
        setHistory([]);
        setAuditLog([]);
        loadHistory(id);
        loadAuditLog(id);
      } catch (e) {
        notification.error({ message: `Alert #${id} not found.` });
        navigate('/alerts', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertIdParam]);

  function formatHours(hours: number): string {
    if (hours == null || isNaN(hours)) return '?';
    if (hours < 1) return '<1h';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remH = hours % 24;
    if (days < 7) {
      return remH > 0 ? `${days}d ${remH}h` : `${days}d`;
    }
    const weeks = Math.floor(days / 7);
    const remD = days % 7;
    return remD > 0 ? `${weeks}w ${remD}d` : `${weeks}w`;
  }

  function renderAlertSummary(record: Alert): React.ReactNode | null {
    const data =
      typeof record.alertData === 'string'
        ? (() => {
            try {
              return JSON.parse(record.alertData);
            } catch {
              return {};
            }
          })()
        : record.alertData || {};
    const slug = record.alertType?.slug;
    switch (slug) {
      case 'domain_not_renewed_rejected':
      case 'domain_not_renewed_acknowledged': {
        const domains: string[] = data.domains || [];
        if (domains.length === 0) return null;
        if (domains.length <= 2) return domains.join(', ');
        return `${domains[0]}, ${domains[1]} +${domains.length - 2} more`;
      }
      case 'pick_volume_anomaly': {
        const pct = data.percentChange;
        if (pct == null) return null;
        const dir = pct > 0 ? '+' : '';
        return `${dir}${Math.round(pct)}% (${data.currentCount ?? '?'} picks)`;
      }
      case 'support_no_response':
        return data.daysPending != null ? `${data.daysPending}d pending` : null;
      case 'nc_api_token_invalid':
        return data.username || null;
      case 'nc_account_balance_low': {
        const u = data.username || `account ${data.accountId}`;
        const bal =
          data.availableBalance != null
            ? `$${Number(data.availableBalance).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
            : '?';
        return `${u} · ${bal}`;
      }
      case 'parking_data_stale':
        return data.provider ? `${data.provider} (${formatHours(data.hoursSinceLast)})` : null;
      case 'duplicate_payout': {
        const domain = data.domain || '';
        const amount = data.amount != null ? `$${Number(data.amount).toLocaleString()}` : '';
        const count = data.count || '';
        const venue = data.venue || '';
        const label = [amount, count ? `×${count}` : '', venue].filter(Boolean).join(' · ');
        return (
          <span>
            {domain && (
              <a
                href={`/sales/activity?domain=${encodeURIComponent(domain)}&type=payout`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent hover:opacity-80"
              >
                {domain}
              </a>
            )}
            {label && <>{domain ? ' · ' : ''}{label}</>}
          </span>
        );
      }
      case 'delist_failure': {
        return data.domain || null;
      }
      case 'autorenew_expiring_soon': {
        const domains: { domain: string; daysLeft?: number }[] = data.domains || [];
        if (domains.length === 0) return null;
        const first = domains[0];
        const label = first.daysLeft != null ? `${first.domain} (${first.daysLeft}d)` : first.domain;
        if (domains.length === 1) return label;
        return `${label} +${domains.length - 1} more`;
      }
      case 'payout_data_stale':
      case 'sales_data_stale': {
        let provider = data.provider || '';
        let account = data.account || '';
        if (!provider && record.alertKey) {
          const parts = record.alertKey.split(':');
          if (parts.length >= 2) provider = parts[1];
          if (parts.length >= 3) account = parts[2];
        }
        const label = account ? `${provider}:${account}` : provider;
        const hours = formatHours(data.hoursSinceLast);
        return label ? `${label} (${hours})` : hours !== '?' ? `(${hours})` : null;
      }
      default:
        return null;
    }
  }

  function renderAlertData(data: any) {
    if (!data) return '-';
    return (
      <pre className="text-2xs max-h-[300px] overflow-auto m-0 font-mono text-text-2 bg-bg p-2 rounded-md border border-border">
        {prettyJson(data)}
      </pre>
    );
  }

  const typeOptions = [
    { value: '', label: 'All Types' },
    ...alertTypes.map((t) => ({ value: String(t.id), label: t.name })),
  ];

  const statusLabel = statusOptions.find((o) => o.value === statusFilter)?.label || 'All';
  const typeLabel =
    typeOptions.find((o) => o.value === String(alertTypeFilter ?? ''))?.label || 'All Types';

  const columns: Column<Alert>[] = [
    {
      key: 'id',
      title: 'ID',
      width: 70,
      render: (a) => (
        <button type="button" onClick={() => openDetail(a)} className="font-mono text-xs text-accent hover:opacity-80 tabular-nums">
          #{a.id}
        </button>
      ),
    },
    {
      key: 'type',
      title: 'Type',
      render: (a) => {
        const summary = renderAlertSummary(a);
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-text font-medium text-sm">
              {a.alertType?.name || `Type #${a.alertTypeId}`}
            </span>
            {summary && <span className="text-text-3 text-2xs">{summary}</span>}
          </div>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      width: 110,
      render: (a) => <StatusTag tone={statusTone[a.status] ?? 'neutral'}>{a.status}</StatusTag>,
    },
    {
      key: 'triggered',
      title: 'Triggered',
      width: 160,
      render: (a) => (
        <span
          className="font-mono text-xs text-text-2 tabular-nums"
          title={moment(a.triggeredAt).format('LLL')}
        >
          {moment(a.triggeredAt).fromNow()}
        </span>
      ),
    },
    {
      key: 'notes',
      title: 'Notes',
      render: (a) =>
        a.notes ? (
          <span className="text-text-2 text-sm truncate block max-w-[300px]">{a.notes}</span>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      width: 100,
      render: (a) =>
        (a.status === 'active' || a.status === 'resolved') ? (
          <Button size="sm" variant="danger" onClick={() => handleDismiss(a)}>
            Dismiss
          </Button>
        ) : null,
    },
  ];

  const activeFilterCount =
    (statusFilter ? 1 : 0) + (alertTypeFilter != null ? 1 : 0) + (search ? 1 : 0);

  return (
    <>
      <PageHead
        title="Alerts"
        subtitle="System alerts triggered by anomalies, stale data, and operator actions."
      />

      <PageToolbar>
        <SelectPill
          label="Status"
          value={statusLabel}
          options={statusOptions}
          selected={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        />
        <SelectPill
          label="Type"
          value={typeLabel}
          options={typeOptions}
          selected={alertTypeFilter != null ? String(alertTypeFilter) : ''}
          onChange={(v) => {
            setAlertTypeFilter(v ? Number(v) : undefined);
            setPage(1);
          }}
        />
        <input
          type="text"
          value={search}
          placeholder="Search alerts…"
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="bg-bg-elevated border border-transparent rounded-md px-3 py-1 w-[240px] text-sm text-text outline-none focus:border-border-strong placeholder:text-text-3"
        />

        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('');
              setAlertTypeFilter(undefined);
              setSearch('');
              setPage(1);
            }}
            className="text-2xs text-text-3 hover:text-text font-mono px-1"
          >
            clear ({activeFilterCount})
          </button>
        )}

        <span className="ml-auto text-2xs text-text-3 font-mono">
          {alerts ? (
            <>
              total <b className="text-text-2">{alerts.total.toLocaleString()}</b>
            </>
          ) : (
            'loading…'
          )}
        </span>
      </PageToolbar>

      <DataTable
        columns={columns}
        data={alerts?.content ?? []}
        rowKey={(a) => String(a.id)}
        loading={loading}
        emptyMessage="No alerts match these filters."
      />
      <PageFooter
        left={
          <span>
            Showing <b className="text-text-2">{alerts?.content.length ?? 0}</b> of{' '}
            <b className="text-text-2">{alerts?.total.toLocaleString() ?? 0}</b>
          </span>
        }
        right={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={alerts?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            loading={loading}
          />
        }
      />

      <Modal
        title={
          detailModal
            ? `Alert #${detailModal.id} — ${detailModal.alertType?.name || ''}`
            : 'Alert Details'
        }
        open={!!detailModal}
        onCancel={closeDetail}
        footer={null}
        width={840}
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      >
        {detailModal && (
          <div className="text-sm text-text">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <DetailRow label="Status">
                <StatusTag tone={statusTone[detailModal.status] ?? 'neutral'}>
                  {detailModal.status}
                </StatusTag>
              </DetailRow>
              <DetailRow label="Triggered">
                <span className="font-mono text-xs text-text-2 tabular-nums">
                  {moment(detailModal.triggeredAt).format('LLL')}
                </span>
              </DetailRow>
              {detailModal.resolvedAt && (
                <DetailRow label="Resolved">
                  <span className="font-mono text-xs text-text-2 tabular-nums">
                    {moment(detailModal.resolvedAt).format('LLL')}
                  </span>
                </DetailRow>
              )}
              {detailModal.dismissedAt && (
                <DetailRow label="Dismissed">
                  <span className="font-mono text-xs text-text-2 tabular-nums">
                    {moment(detailModal.dismissedAt).format('LLL')}
                  </span>
                </DetailRow>
              )}
            </div>

            {detailModal.alertType?.description && (
              <div className="mb-4 text-text-2 text-xs">{detailModal.alertType.description}</div>
            )}

            <div className="mb-4">
              <div className="text-2xs uppercase tracking-wider text-text-3 font-semibold mb-1.5">
                Alert Data
              </div>
              {detailModal.alertType?.slug === 'support_no_response' &&
                (() => {
                  const data =
                    typeof detailModal.alertData === 'string'
                      ? (() => {
                          try {
                            return JSON.parse(detailModal.alertData);
                          } catch {
                            return null;
                          }
                        })()
                      : detailModal.alertData;
                  return data?.jobId ? (
                    <div className="mb-2">
                      <Link
                        to={`/emails/support-jobs/${data.jobId}`}
                        className="text-accent text-sm hover:opacity-80"
                      >
                        View Support Job #{data.jobId}
                      </Link>
                    </div>
                  ) : null;
                })()}
              {detailModal.alertType?.slug === 'duplicate_payout' &&
                (() => {
                  const data =
                    typeof detailModal.alertData === 'string'
                      ? (() => { try { return JSON.parse(detailModal.alertData); } catch { return null; } })()
                      : detailModal.alertData;
                  if (!data?.domain) return null;
                  const amount = data.amount != null ? `$${Number(data.amount).toLocaleString()}` : '';
                  const count = data.count || '';
                  const venue = data.venue || '';
                  const account = data.account || '';
                  return (
                    <div className="mb-3 p-2.5 bg-bg rounded-md border border-border">
                      <div className="flex items-center gap-2 mb-1.5">
                        <a
                          href={`/sales/activity?domain=${encodeURIComponent(data.domain)}&type=payout`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent font-medium text-sm hover:opacity-80"
                        >
                          {data.domain} →
                        </a>
                      </div>
                      <div className="text-xs text-text-2 flex gap-3">
                        {amount && <span>Amount: <b className="text-text">{amount}</b></span>}
                        {count && <span>Count: <b className="text-text">×{count}</b></span>}
                        {venue && <span>Venue: <b className="text-text">{venue}</b></span>}
                        {account && <span>Account: <b className="text-text">{account}</b></span>}
                      </div>
                      {data.firstDate && data.lastDate && (
                        <div className="text-2xs text-text-3 mt-1">
                          {moment(data.firstDate).format('MMM D, YYYY')} — {moment(data.lastDate).format('MMM D, YYYY')}
                        </div>
                      )}
                    </div>
                  );
                })()}
              {(() => {
                const data =
                  typeof detailModal.alertData === 'string'
                    ? (() => {
                        try {
                          return JSON.parse(detailModal.alertData);
                        } catch {
                          return null;
                        }
                      })()
                    : detailModal.alertData;
                if (!data || data.hoursSinceLast == null) return null;
                return (
                  <div className="mb-2 text-xs">
                    <span className="text-text-3">Time since last data: </span>
                    <span className="text-text font-mono">{formatHours(data.hoursSinceLast)}</span>
                    {data.lastDataAt && (
                      <span className="text-text-3 ml-2">
                        (last: {moment(data.lastDataAt).format('LLL')},{' '}
                        {moment(data.lastDataAt).fromNow()})
                      </span>
                    )}
                  </div>
                );
              })()}
              {detailModal.alertType?.slug === 'autorenew_expiring_soon' &&
                (() => {
                  const data =
                    typeof detailModal.alertData === 'string'
                      ? (() => { try { return JSON.parse(detailModal.alertData); } catch { return null; } })()
                      : detailModal.alertData;
                  const rows = Array.isArray(data?.domains) ? data.domains : [];
                  return rows.length > 0 ? <AutoRenewExpiringTable rows={rows} /> : null;
                })()}
              {detailModal.alertType?.slug === 'autorenew_expiring_soon' ? (
                <Collapse
                  size="small"
                  ghost
                  items={[
                    {
                      key: 'raw',
                      label: <span className="text-2xs">Raw alert data</span>,
                      children: renderAlertData(detailModal.alertData),
                    },
                  ]}
                />
              ) : (
                renderAlertData(detailModal.alertData)
              )}
            </div>

            <div className="mb-4">
              <div className="text-2xs uppercase tracking-wider text-text-3 font-semibold mb-1.5">
                History ({history.length})
              </div>
              {historyLoading ? (
                <Spin size="small" />
              ) : history.length > 0 ? (
                <div className="max-h-[280px] overflow-y-auto pr-1">
                  <Timeline
                    items={history.map((h, i) => ({
                      color: i === history.length - 1 ? 'blue' : 'gray',
                      children: (
                        <div>
                          <span className="text-2xs text-text-3 font-mono">
                            {moment(h.createdAt).format('LLL')}
                            {i === 0 && ' (initial)'}
                          </span>
                          <Collapse
                            size="small"
                            ghost
                            items={[
                              {
                                key: h.id,
                                label: <span className="text-2xs">View data</span>,
                                children: renderAlertData(h.alertData),
                              },
                            ]}
                          />
                        </div>
                      ),
                    }))}
                  />
                </div>
              ) : (
                <div className="text-text-3 text-xs">No history entries.</div>
              )}
            </div>

            <div className="mb-4">
              <div className="text-2xs uppercase tracking-wider text-text-3 font-semibold mb-1.5">
                Notes
              </div>
              <TextArea
                rows={3}
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
              />
              <div className="mt-2">
                <Button
                  variant="primary"
                  size="sm"
                  loading={savingNotes}
                  onClick={handleSaveNotes}
                >
                  Save Notes
                </Button>
              </div>
            </div>

            <Collapse
              size="small"
              className="mb-4"
              items={[
                {
                  key: 'audit-log',
                  label: (
                    <span className="text-2xs uppercase tracking-wider text-text-2 font-semibold">
                      Audit Log ({auditLog.length})
                    </span>
                  ),
                  children: auditLogLoading ? (
                    <Spin size="small" />
                  ) : auditLog.length > 0 ? (
                    <div className="max-h-[280px] overflow-y-auto">
                      <Timeline
                        items={auditLog.map((entry) => ({
                          color: 'gray',
                          children: (
                            <div>
                              <div className="flex items-center gap-2">
                                <StatusTag tone="neutral" dot={false}>
                                  {entry.auditEventType}
                                </StatusTag>
                                <span className="text-2xs text-text-3 font-mono">
                                  {moment(entry.createdAt).format('LLL')}
                                </span>
                              </div>
                              {entry.data && entry.data !== '' && (
                                <pre className="text-2xs mt-1 mb-0 font-mono text-text-3">
                                  {prettyJson(entry.data)}
                                </pre>
                              )}
                            </div>
                          ),
                        }))}
                      />
                    </div>
                  ) : (
                    <span className="text-text-3 text-xs">No audit log entries.</span>
                  ),
                },
              ]}
            />

            {(detailModal.status === 'active' || detailModal.status === 'resolved') && (
              <div className="flex justify-end pt-2 border-t border-border">
                <Button variant="danger" loading={dismissing} onClick={() => handleDismiss(detailModal)}>
                  Dismiss Alert
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

interface DetailRowProps {
  label: string;
  children: React.ReactNode;
}

function DetailRow({ label, children }: DetailRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-2xs uppercase tracking-wider text-text-3 font-semibold">
        {label}
      </span>
      <span>{children}</span>
    </div>
  );
}

interface SelectPillProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  selected: string;
  onChange: (value: string) => void;
}

function SelectPill({ label, value, options, selected, onChange }: SelectPillProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={
        <div className="p-1 flex flex-col min-w-[180px] max-h-[320px] overflow-auto">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`text-left px-2 py-1.5 text-sm rounded hover:bg-bg-elevated ${
                selected === opt.value ? 'text-accent font-semibold' : 'text-text-2'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      }
    >
      <span>
        <FilterPill label={label} value={value} active={!!selected} />
      </span>
    </Popover>
  );
}

export default Alerts;
